import { useQuery } from '@apollo/client';
import { useConsentFromDatabridge, CONSENT_IDENTIFIERS } from '@xp-utilities/web';
import isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';
import React, { useContext, useEffect, useState } from 'react';
import BreakpointContext from '../../context/breakpoint';
import useDigitalData from '../../hooks/useDigitalData';
import parsePlacementIds from '../../tools/parsePlacementIds';
import { DD_HAS_ALGONOMY_REFETCH_ON_CONSENT_INTERACTION_ENABLED } from '../DigitalDataProvider';
import ProductGridContextProvider from '../ProductGridContextProvider';
import { mySavesItemType } from '../types';
import CategoryRecommendationsTabs from './CategoryRecommendationsTabs';
import HomePageAEMRecommendations from './HomePageAEMRecommendations';
import CATEGORY_RECOMMENDATIONS_QUERY, { RECOMMENDATIONS_CONFIG_QUERY } from './queries';

const PLACEMENT_ID_ATTRIBUTE = 'data-placement-id';

function CategoryRecommendations({
  isXpProductCardEnabled,
  placementIds,
  homePageRecSliderNodes = [],
  isMfeQuickview,
  mySavesItems,
  refetchMySaves,
}) {
  const breakpoints = useContext(BreakpointContext);
  const [hasUserConsent, setHasUserConsent] = useState(null);

  const {
    [DD_HAS_ALGONOMY_REFETCH_ON_CONSENT_INTERACTION_ENABLED]: hasRefetchOnConsentInteractionEnabled,
  } = useDigitalData([DD_HAS_ALGONOMY_REFETCH_ON_CONSENT_INTERACTION_ENABLED]);

  const { data: recommendationsConfig } = useQuery(RECOMMENDATIONS_CONFIG_QUERY);

  const recommendationsHideSwatchesMasterFlag = recommendationsConfig?.config
    ?.recommendationsHideSwatchesMasterFlag.value ?? {};
  const enableSecondRecommendationsContainerOnHomePage = recommendationsConfig?.config
    ?.enableSecondRecommendationsContainerOnHomePage.value ?? false;

  useConsentFromDatabridge(CONSENT_IDENTIFIERS.TARGETING_COOKIES, (consent) => {
    // to prevent unnecessary re-renders,
    // we only update the state if the consent is not already a boolean
    if (hasRefetchOnConsentInteractionEnabled) setHasUserConsent(consent);
    else setHasUserConsent((prev) => (typeof prev === 'boolean' ? prev : consent));
  });

  const placements = parsePlacementIds(placementIds, breakpoints);

  // e.g. aemPlacements = ['home_page.rr2', 'home_page.rr1']
  const aemPlacements = homePageRecSliderNodes.reduce(
    (acc, ypbTargetNode) => acc.concat(
      parsePlacementIds(
        ypbTargetNode.getAttribute(PLACEMENT_ID_ATTRIBUTE),
        breakpoints,
      ),
    ),
    [],
  );

  const {
    data,
  } = useQuery(CATEGORY_RECOMMENDATIONS_QUERY, {
    // Execute query only if variables are properly formed
    skip: isEmpty(placements) || typeof hasUserConsent !== 'boolean',
    variables: {
      placements: [...placements, ...aemPlacements],
      hasUserConsent,
      hideSwatches: !recommendationsHideSwatchesMasterFlag?.homePageRecs,
    },
  });

  const placementData = data?.categoryRecommendations?.placements
    ?.filter((placement) => placements.includes(placement.id))
    ?.map((item) => ({ ...item, id: String(item.id) }));

  // Only get placement data for AEM placements if target nodes contain the placementIds
  const aemPlacementData = (aemPlacements.length && data?.categoryRecommendations?.placements
    ?.filter((placement) => aemPlacements.includes(placement.id))) || [];

  const debug = data?.categoryRecommendations?.algonomyDebug;

  useEffect(() => {
    if (!window?.digitalData || !debug) return;

    let parsedDebugData = null;

    try {
      parsedDebugData = JSON.parse(debug);
    } catch { /* do nothing */ }

    const { algonomyQueryParams: { placements: debugPlacement = '' } = {} } = parsedDebugData || {};
    const debugId = debugPlacement.split('|')[0];

    window.digitalData.set(`algonomy.debug.${debugId}`, parsedDebugData);
  }, [debug]);

  if (!placementData) {
    return null;
  }

  return (
    <ProductGridContextProvider>
      <>
        <CategoryRecommendationsTabs
          isMfeQuickview={isMfeQuickview}
          isXpProductCardEnabled={isXpProductCardEnabled}
          mvtDetailed={data?.categoryRecommendations?.mvtDetailed}
          mySavesItems={mySavesItems}
          placementData={placementData}
          rcs={data?.categoryRecommendations?.rcs}
          refetchMySaves={refetchMySaves}
          userId={data?.categoryRecommendations?.userId}
        />
        {
          enableSecondRecommendationsContainerOnHomePage && aemPlacementData.length ? (
            <HomePageAEMRecommendations
              aemPlacementData={aemPlacementData}
              homePageRecSliderNodes={homePageRecSliderNodes}
            />
          ) : null
        }
      </>
    </ProductGridContextProvider>
  );
}

export default CategoryRecommendations;

CategoryRecommendations.propTypes = {
  // null is for loading state of flag fetch
  isXpProductCardEnabled: PropTypes.oneOf([true, false, null]),
  isMfeQuickview: PropTypes.bool,
  mySavesItems: mySavesItemType,
  refetchMySaves: PropTypes.func,
  placementIds: PropTypes.string.isRequired,
  homePageRecSliderNodes: PropTypes.arrayOf(
    PropTypes.instanceOf(typeof Element === 'undefined' ? Object : Element),
  ),
};
